import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatEUR, formatDate } from "@/lib/constants";
import { toast } from "sonner";
import { ChevronRight, FileText, Plus, AlertCircle, Loader2 } from "lucide-react";

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const nav = useNavigate();

  const load = async () => {
    const [oRes, lRes] = await Promise.all([api.get("/offers"), api.get("/leads")]);
    setOffers(oRes.data);
    setLeads(lRes.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const eligibleLeads = useMemo(() => {
    const leadIdsWithOffer = new Set(offers.map((o) => o.lead_id));
    return leads.filter((l) => !leadIdsWithOffer.has(l.lead_id) && (l.missing_fields?.length || 0) === 0 && !["Verloren", "Gewonnen"].includes(l.status));
  }, [leads, offers]);

  const createOffer = async (leadId) => {
    setBusy(leadId);
    try {
      const r = await api.post(`/leads/${leadId}/generate-offer`);
      toast.success("Angebotsentwurf erstellt");
      setOpen(false);
      nav(`/offers/${r.data.offer_id}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6" data-testid="offers-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Angebote</h1>
          <p className="mt-1 text-slate-600">Alle erstellten Angebote und deren Status</p>
        </div>
        <Button data-testid="new-offer-btn" onClick={() => setOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> Neues Angebot erstellen
        </Button>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr className="text-left">
                <th className="px-6 py-3 font-semibold">Kunde</th>
                <th className="px-6 py-3 font-semibold">Titel</th>
                <th className="px-6 py-3 font-semibold text-right">Wert</th>
                <th className="px-6 py-3 font-semibold">Status</th>
                <th className="px-6 py-3 font-semibold">Versendet</th>
                <th className="px-6 py-3 font-semibold text-right">Aufrufe</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan="7" className="px-6 py-10 text-center text-slate-500">Wird geladen …</td></tr>
              ) : offers.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-16 text-center">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">Noch keine Angebote erstellt.</p>
                  <Button data-testid="empty-create-offer" onClick={() => setOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Erstes Angebot erstellen
                  </Button>
                </td></tr>
              ) : offers.map((o) => (
                <tr key={o.offer_id} data-testid={`offer-row-${o.offer_id}`} onClick={() => nav(`/offers/${o.offer_id}/edit`)} className="hover:bg-slate-50 cursor-pointer">
                  <td className="px-6 py-4 font-medium text-slate-900">{o.customer_name || "—"}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{o.title}</td>
                  <td className="px-6 py-4 text-right tabular-nums">{formatEUR(o.total_price)}</td>
                  <td className="px-6 py-4"><StatusBadge status={o.status} /></td>
                  <td className="px-6 py-4 text-sm text-slate-600">{formatDate(o.sent_at)}</td>
                  <td className="px-6 py-4 text-right text-sm">{o.opened_count}</td>
                  <td className="px-6 py-4 text-right text-slate-400"><ChevronRight className="w-4 h-4 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <p className="px-4 py-8 text-center text-slate-500 text-sm">Wird geladen …</p>
          ) : offers.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-3 text-sm">Noch keine Angebote.</p>
              <Button data-testid="empty-create-offer-mobile" onClick={() => setOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
                <Plus className="w-4 h-4 mr-2" /> Erstes Angebot
              </Button>
            </div>
          ) : offers.map((o) => (
            <button
              key={o.offer_id}
              data-testid={`offer-card-${o.offer_id}`}
              onClick={() => nav(`/offers/${o.offer_id}/edit`)}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <p className="font-semibold text-slate-900 truncate">{o.customer_name || "—"}</p>
                <span className="text-sm font-semibold tabular-nums whitespace-nowrap">{formatEUR(o.total_price)}</span>
              </div>
              <p className="text-xs text-slate-500 truncate mb-1.5">{o.title}</p>
              <div className="flex items-center justify-between gap-2">
                <StatusBadge status={o.status} />
                <span className="text-xs text-slate-500">{o.sent_at ? `${formatDate(o.sent_at)} · ${o.opened_count}× geöffnet` : "—"}</span>
              </div>
            </button>
          ))}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-offer-dialog">
          <DialogHeader>
            <DialogTitle>Angebot für welchen Lead?</DialogTitle>
            <DialogDescription>
              Wähle einen Lead mit vollständigen Daten – der Entwurf wird automatisch generiert und du landest direkt im Editor.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2 space-y-2">
            {eligibleLeads.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-500">
                <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                Aktuell kein passender Lead. Erfasse zuerst einen Lead mit vollständigen PV-Daten – oder ergänze fehlende Angaben in einem bestehenden Lead.
                <div className="mt-4">
                  <Button data-testid="dialog-new-lead" onClick={() => { setOpen(false); nav("/leads/new"); }} variant="outline" size="sm">
                    Neuen Lead anlegen
                  </Button>
                </div>
              </div>
            ) : eligibleLeads.map((l) => (
              <button
                key={l.lead_id}
                data-testid={`pick-lead-${l.lead_id}`}
                onClick={() => createOffer(l.lead_id)}
                disabled={!!busy}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-orange-300 hover:bg-orange-50/40 transition-colors text-left disabled:opacity-50"
              >
                <div>
                  <p className="font-semibold text-slate-900">{l.first_name} {l.last_name}</p>
                  <p className="text-xs text-slate-500">{l.address || l.email} · {l.annual_consumption || "?"} kWh</p>
                </div>
                {busy === l.lead_id ? <Loader2 className="w-4 h-4 animate-spin text-orange-600" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
